// ============================================
// Seed Templates: MSO / PC Documents
// Category: mso-pc
// Templates: 6
// ============================================

import type { DocTemplate, QualityGate } from '../../types/docops';

const MSA_QUALITY_GATE: QualityGate = {
  requiredScore: 95,
  maxAttempts: 3,
  provisions: [
    { id: 'msa-cpom', title: 'CPOM Firewall', required: true, keywords: ['does not practice medicine', 'corporate practice', 'clinical decision'], weight: 'critical' },
    { id: 'msa-clinical-authority', title: 'PC Retains Clinical Authority', required: true, keywords: ['clinical authority', 'clinical oversight', 'clinical control', 'medical judgment'], weight: 'critical' },
    { id: 'msa-fixed-retainer', title: 'Fixed Retainer (Not Percentage)', required: true, keywords: ['fixed', 'retainer', 'monthly', 'fair market value'], weight: 'critical' },
    { id: 'msa-assignment', title: 'Assignment Clause (M&A)', required: true, keywords: ['assign', 'merger', 'acquisition', 'without consent'], weight: 'critical' },
    { id: 'msa-ip-ownership', title: 'Non-Clinical IP Ownership', required: true, keywords: ['intellectual property', 'non-clinical', 'belongs to'], weight: 'critical' },
    { id: 'msa-anti-kickback', title: 'Anti-Kickback Safe Harbor', required: true, keywords: ['anti-kickback', 'safe harbor', 'fair market value', 'kickback'], weight: 'critical' },
    { id: 'msa-services', title: 'MSO Services Scope', required: true, keywords: ['technology platform', 'billing', 'scheduling', 'administrative'], weight: 'high' },
    { id: 'msa-termination', title: 'Termination Provisions', required: true, keywords: ['termination', 'terminate', '180 day', 'cause'], weight: 'high' },
    { id: 'msa-coc', title: 'Change of Control', required: true, keywords: ['change of control', 'acquisition', '90 day'], weight: 'high' },
    { id: 'msa-indemnification', title: 'Mutual Indemnification', required: true, keywords: ['indemnif', 'hold harmless', 'mutual'], weight: 'high' },
    { id: 'msa-noncompete', title: 'PC Non-Compete', required: true, keywords: ['non-compete', 'noncompete', 'restrictive', '24 month'], weight: 'high' },
    { id: 'msa-poa', title: 'Limited Power of Attorney', required: true, keywords: ['power of attorney', 'poa', 'unresponsive', '7 business day'], weight: 'high' },
  ],
};

export const SEED_TEMPLATES_MSO_PC: DocTemplate[] = [
  // ── 4.1 Management Services Agreement (MSA) ──
  {
    id: '4.1-management-services-agreement',
    categoryId: 'mso-pc',
    qualityGate: MSA_QUALITY_GATE,
    title: 'Management Services Agreement (MSA)',
    description:
      'Core agreement between the MSO (LLC) and the Professional Corporation defining management services, clinical independence, revenue flow, and the critical assignment clause for M&A flexibility.',
    templateContent: `# Management Services Agreement

MSO: {{brandEntity}} (CEO: {{ceoName}})
PC: {{pcEntityName}} (Owner: {{pcOwnerName}})

## 1. MSO Services
{{AI_GENERATED_CONTENT}}

## 2. PC Retained Authority
Clinical oversight by Medical Director ({{medicalDirectorName}}).
{{AI_GENERATED_CONTENT}}

## 3. CORPORATE PRACTICE OF MEDICINE FIREWALL ⚠️ CRITICAL
{{brandEntity}} does NOT practice medicine or direct clinical decisions.
{{AI_GENERATED_CONTENT}}

## 4. Financial Terms
PC Retainer: ${'$'}{{pcRetainer}}/month (fixed — anti-kickback safe harbor, fair market value).
{{AI_GENERATED_CONTENT}}

## 5. Assignment Clause ⚠️ CRITICAL
{{brandEntity}} may assign this Agreement in M&A WITHOUT PC consent.
{{AI_GENERATED_CONTENT}}

## 6. Termination
{{AI_GENERATED_CONTENT}}

## 7. Change of Control
{{AI_GENERATED_CONTENT}}

## 8. Non-Clinical IP Ownership
{{AI_GENERATED_CONTENT}}

## 9. PC Non-Compete
{{AI_GENERATED_CONTENT}}

## 10. Compliance Obligations
{{AI_GENERATED_CONTENT}}

## 11. Limited Power of Attorney
{{AI_GENERATED_CONTENT}}

## 12. Indemnification
{{AI_GENERATED_CONTENT}}

## 13. Insurance
{{AI_GENERATED_CONTENT}}

## 14. Term
10-year initial + 5-year auto-renewal.
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'brandEntity',
      'ceoName',
      'pcEntityName',
      'pcOwnerName',
      'medicalDirectorName',
      'telehealthNetwork',
      'pcRetainer',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate MSA OUTLINE for health-tech attorney review. MSO: {{brandEntity}} (CEO: {{ceoName}}), PC: {{pcEntityName}} (Owner: {{pcOwnerName}}). MSO services: technology platform, patient scheduling/intake, billing/revenue collection, vendor management ({{telehealthNetwork}}, pharmacy), marketing/acquisition, admin operations, compliance infrastructure, customer support. PC retains: clinical oversight (Medical Director), clinical protocol approval, provider quality standards. Financials: all revenue collected by {{brandEntity}}, MSO pays all expenses, PC receives fixed monthly retainer ${{pcRetainer}} (NOT percentage-based — anti-kickback safe harbor, fair market value). KEY PROVISIONS: 1) CORPORATE PRACTICE OF MEDICINE FIREWALL — {{brandEntity}} does not practice medicine or direct clinical decisions, 2) PC retains full clinical authority, 3) Assignment clause — {{brandEntity}} may assign in M&A WITHOUT PC consent, 4) Termination 180d without cause / 30d with cause + cure, 5) Change of control — acquirer may terminate MSA 90d post-acquisition, 6) PC cooperation in transition, 7) Non-clinical IP belongs to {{brandEntity}}, 8) PC non-compete during term + 24mo, 9) Compliance obligations with consequences per addendum, 10) Limited POA if PC unresponsive beyond 7 business days, 11) Mutual indemnification, 12) Insurance, 13) 10-year initial + 5-year auto-renewal. Format: detailed outline with commentary, flag assignment clause and CPOM firewall as critical.',
    attorneyReviewRequired: true,
    launchBlocker: true,
    estimatedPages: 25,
    sortOrder: 1,
    generationRound: 3,
    dependencies: ['1.1-terms-of-service', '2.1-operating-agreement'],
    anchorSections: ['§3', '§4', '§9'],
    inheritedProvisions: ['cpom-firewall', 'clinical-liability-carveout'],
  },

  // ── 4.2 Medical Director Agreement ──
  {
    id: '4.2-medical-director-agreement',
    categoryId: 'mso-pc',
    title: 'Medical Director Agreement',
    description:
      'Agreement with the Medical Director for clinical oversight, protocol review, brand ambassador duties, and regulatory compliance. Independent contractor arrangement.',
    templateContent: `# Medical Director Agreement

Medical Director: {{medicalDirectorName}} ({{mdCredentials}})
Licensed in: {{mdLicenseStates}}
Company: {{brandEntity}} (d/b/a {{brandName}})

## 1. Duties
{{AI_GENERATED_CONTENT}}

## 2. Compensation
${'$'}{{mdRetainer}}/month (~10-15 hrs/month)

## 3. IP Assignment
{{AI_GENERATED_CONTENT}}

## 4. Non-Compete
{{AI_GENERATED_CONTENT}}

## 5. Non-Solicit
{{AI_GENERATED_CONTENT}}

## 6. Confidentiality
{{AI_GENERATED_CONTENT}}

## 7. Compliance Obligations
{{AI_GENERATED_CONTENT}}

## 8. Likeness / Brand Rights
{{AI_GENERATED_CONTENT}}

## 9. Change of Control Bonus
{{AI_GENERATED_CONTENT}}

## 10. Termination
{{AI_GENERATED_CONTENT}}

## 11. Insurance
{{AI_GENERATED_CONTENT}}

## 12. Independent Contractor
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'medicalDirectorName',
      'mdCredentials',
      'mdLicenseStates',
      'brandEntity',
      'brandName',
      'mdRetainer',
    ],
    generationPrompt:
      'Generate Medical Director Agreement OUTLINE. MD: {{medicalDirectorName}} ({{mdCredentials}}), Licensed in: {{mdLicenseStates}}, Company: {{brandEntity}} (d/b/a {{brandName}}). Duties: review/approve clinical protocols, review educational content for clinical accuracy, clinical QA oversight, named Medical Director for regulatory purposes, quarterly review calls, spokesperson/brand ambassador per addendum, NOT prescribing or direct patient care unless specifically engaged. Compensation: ${{mdRetainer}}/month, ~10-15 hrs/month. Provisions: 1) IP Assignment (work-for-hire), 2) Non-compete 24mo competing AI health platforms, 3) Non-solicit 24mo, 4) Perpetual confidentiality, 5) Compliance obligations per addendum, 6) Likeness/brand rights per spokesperson addendum, 7) Change of control bonus, 8) Termination 90d without cause / 30d with cause, 9) MD maintains malpractice insurance, 10) Independent contractor. Format: outline with attorney notes.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 2,
    generationRound: 2,
    dependencies: ['1.1-terms-of-service', '2.1-operating-agreement'],
    anchorSections: ['§3', '§4', '§9'],
    inheritedProvisions: ['cpom-firewall', 'clinical-liability-carveout'],
  },

  // ── 4.3 PC Compliance Addendum ──
  {
    id: '4.3-compliance-addendum-pc',
    categoryId: 'mso-pc',
    title: 'PC Compliance Addendum',
    description:
      'Addendum to the MSA detailing compliance obligations for the PC including license maintenance, HIPAA training, BAA requirements, and escalating consequences for non-compliance.',
    templateContent: `# Compliance Addendum

Addendum to the Management Services Agreement between
{{brandEntity}} (MSO) and {{pcEntityName}} (PC)

## 1. PC Compliance Obligations
{{AI_GENERATED_CONTENT}}

## 2. Reporting Requirements
{{AI_GENERATED_CONTENT}}

## 3. Consequences for Non-Compliance
| Level | Action | Timeline |
|-------|--------|----------|
| 1 | Written Warning | Immediate |
| 2 | Cure Period | [X] days |
| 3 | Retainer Reduction | Per schedule |
| 4 | MSA Termination | Per MSA terms |

{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: ['pcEntityName', 'brandEntity'],
    generationPrompt:
      'Generate Compliance Addendum for {{pcEntityName}} partnership with {{brandEntity}}. Obligations: maintain active medical licenses in all operating states, complete HIPAA training annually, sign BAA and maintain compliance, respond to document signing requests within 7 business days, cooperate with regulatory audits, report any license actions or disciplinary proceedings within 48 hours. Consequences: warning \u2192 cure period \u2192 retainer reduction \u2192 MSA termination. Format: addendum to MSA.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 4,
    sortOrder: 3,
    generationRound: 3,
    dependencies: ['4.1-management-services-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 4.4 Medical Director Spokesperson Addendum ──
  {
    id: '4.4-spokesperson-addendum-md',
    categoryId: 'mso-pc',
    title: 'Medical Director Spokesperson Addendum',
    description:
      'Addendum granting use of the Medical Director\'s name, likeness, and credentials in marketing materials, subject to MD review and approval.',
    templateContent: `# Spokesperson Addendum

Addendum to the Medical Director Agreement between
{{brandEntity}} (d/b/a {{brandName}}) and {{medicalDirectorName}}

## 1. Grant of Rights
{{AI_GENERATED_CONTENT}}

## 2. Restrictions
{{AI_GENERATED_CONTENT}}

## 3. Compensation
Included in base Medical Director retainer.

## 4. Term
Coterminous with Medical Director Agreement.

_________________________
{{medicalDirectorName}} Signature

_________________________
{{brandEntity}} Authorized Signature
`,
    requiredVariables: [
      'medicalDirectorName',
      'brandName',
      'brandEntity',
    ],
    generationPrompt: `Generate a Spokesperson Addendum to the Medical Director Agreement between {{brandEntity}} (d/b/a {{brandName}}) and {{medicalDirectorName}}.

SECTION 1 — GRANT OF RIGHTS:
{{brandEntity}} is granted the right to use {{medicalDirectorName}}'s name, likeness, professional credentials, and title in:
- Website and platform content
- Social media posts and campaigns
- Press releases and media communications
- Investor materials and pitch decks
- Marketing collateral (digital and print)

SECTION 2 — RESTRICTIONS:
- All materials using {{medicalDirectorName}}'s likeness require MD review and written approval before publication
- No implied endorsement of specific supplements or products
- Clinical accuracy review required for any health-related claims
- {{medicalDirectorName}} may revoke approval for specific materials with 30 days written notice
- No materials may imply {{medicalDirectorName}} personally prescribes or treats patients through the platform

COMPENSATION: Included in base Medical Director retainer. No additional fee.
TERM: Coterminous with the Medical Director Agreement.

FORMAT RULES:
- Use actual names ({{medicalDirectorName}}, {{brandEntity}}, {{brandName}}) — NEVER bracket placeholders
- Do NOT include meta-commentary, notes, or instructions in the output
- Professional legal addendum format
- End with signature blocks for both parties`,
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 3,
    sortOrder: 4,
    generationRound: 3,
    dependencies: ['4.2-medical-director-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },

  // ── 4.5 PC Operating Agreement ──
  {
    id: '4.5-pc-operating-agreement',
    categoryId: 'mso-pc',
    title: 'PC Operating Agreement',
    description:
      'Operating agreement for the Professional Corporation limiting its purpose to clinical services and ensuring compliance with corporate practice of medicine doctrine.',
    templateContent: `# Operating Agreement

**{{pcEntityName}}**
A {{stateOfFormation}} Professional Corporation

Sole Owner: {{pcOwnerName}} (Licensed Physician)

## 1. Purpose
{{AI_GENERATED_CONTENT}}

## 2. Management
{{AI_GENERATED_CONTENT}}

## 3. Transfer Restrictions
{{AI_GENERATED_CONTENT}}

## 4. Corporate Practice of Medicine Compliance
{{AI_GENERATED_CONTENT}}

## 5. Clinical Independence
{{AI_GENERATED_CONTENT}}

## 6. Managed Care Contracting
{{AI_GENERATED_CONTENT}}

_________________________
{{pcOwnerName}} Signature
`,
    requiredVariables: [
      'pcEntityName',
      'pcOwnerName',
      'stateOfFormation',
    ],
    generationPrompt:
      'Generate outline for {{pcEntityName}} Operating Agreement in {{stateOfFormation}}. Sole owner: {{pcOwnerName}} (licensed physician). Purpose: provide clinical oversight and medical direction services to affiliated MSO. Key provisions: purpose limited to clinical services, sole member management, no transfer of membership to non-physicians, compliance with corporate practice of medicine doctrine, clinical independence, managed care contracting authority. Format: outline for attorney review.',
    attorneyReviewRequired: true,
    launchBlocker: false,
    estimatedPages: 10,
    sortOrder: 5,
    generationRound: 3,
    dependencies: ['4.1-management-services-agreement'],
    anchorSections: [],
    inheritedProvisions: ['cpom-firewall'],
  },

  // ── 4.6 MSO/PC Compliance Calendar ──
  {
    id: '4.6-mso-pc-compliance-calendar',
    categoryId: 'mso-pc',
    title: 'MSO/PC Compliance Calendar',
    description:
      'Annual compliance calendar tracking all recurring obligations across the MSO, PC, and Medical Director relationship including license verifications, training, and audits.',
    templateContent: `# MSO/PC Compliance Calendar

**{{brandEntity}}** / **{{pcEntityName}}** / **{{medicalDirectorName}}**

| Month | Task | Responsible Party | Due Date | Status |
|-------|------|-------------------|----------|--------|
{{AI_GENERATED_CONTENT}}
`,
    requiredVariables: [
      'brandEntity',
      'pcEntityName',
      'medicalDirectorName',
    ],
    generationPrompt:
      'Generate annual compliance calendar for {{brandEntity}} / {{pcEntityName}} / {{medicalDirectorName}} relationship. Monthly/quarterly/annual tasks: license verifications, BAA renewals, HIPAA training, protocol reviews, retainer payments, compliance audits, insurance renewals, document signing deadlines. Format: 12-month calendar table with responsible party, due date, and status columns.',
    attorneyReviewRequired: false,
    launchBlocker: false,
    estimatedPages: 2,
    sortOrder: 6,
    generationRound: 6,
    dependencies: ['4.1-management-services-agreement'],
    anchorSections: [],
    inheritedProvisions: [],
  },
];
